import type { StopSection } from "~/shared/schemas/shipment-details-schema.ts";

type TimeType = StopSection["timeType"];

/**
 * Renders the appointment row value to mirror the time-window portion of
 * what `StopTimeParser.FormatStopTime` writes into
 * `[Freight].[dbo].[tblPickups].[PickupTime]`. Rules:
 *
 * - `Fcfs` with start only:         `FCFS HH:mm`
 * - `Fcfs` with distinct start/end: `FCFS HH:mm to HH:mm`
 * - `Fcfs` start === end:           `FCFS HH:mm` (DB collapses)
 * - `Appointment` analog:           `Appt …` (mixed-case "Appt", matching legacy)
 * - `Unknown`, or typed but unparseable start: `TBD`
 *
 * Notes are intentionally not appended — only the time-window portion is
 * rendered. Times are formatted in 24-hour `HH:mm` from the parsed `Date`'s
 * local fields to avoid `Intl.DateTimeFormat` leading-zero variance.
 */
export function formatAppointmentWindow(
	timeType: TimeType,
	appointmentStartTime: string | null | undefined,
	appointmentEndTime: string | null | undefined,
): string {
	const start = parseLocalHoursMinutes(appointmentStartTime);

	if (timeType === "Unknown" || start === null) {
		return "TBD";
	}

	const end = parseLocalHoursMinutes(appointmentEndTime);
	const prefix = timeType === "Fcfs" ? "FCFS" : "Appt";
	return end === null || end === start
		? `${prefix} ${start}`
		: `${prefix} ${start} to ${end}`;
}

function parseLocalHoursMinutes(
	value: string | null | undefined,
): string | null {
	if (value == null) {
		return null;
	}

	const trimmed = value.trim();
	if (trimmed.length === 0) {
		return null;
	}

	const date = new Date(trimmed);
	if (Number.isNaN(date.getTime())) {
		return null;
	}

	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");
	return `${hours}:${minutes}`;
}
